import React from 'react';
import classNames from 'classnames';
import get from 'lodash.get';

import { content as ContentPropType } from 'lib/CustomPropTypes';

import LiveBlog from 'components/packages/LiveBlog';
import { Featured } from 'components/packages/CoverSpread/Featured';
import { Tease } from 'components/packages/CoverSpread/Tease';
import { TeaseLayout } from 'components/packages/CoverSpread/TeaseLayouts';
import { Title } from 'components/packages/CoverSpread/Title';

const FEATURED_ITEM_INDEX = 0;
const LIVE_BLOG_INDEX = 1;

const shapeItem = (item = {}, packageMetadata = {}) => ({
  ...item,
  metadata: { // merge content and package metadata
    ...packageMetadata,
    ...(item || {}).metadata,
  },
});

const getFeaturedItem = (items, packageMetadata) => {
  const itemAtIndex = get(items, FEATURED_ITEM_INDEX);
  return shapeItem(itemAtIndex, packageMetadata);
};

const getLiveBlog = (items, packageMetadata) => {
  const itemAtIndex = get(items, LIVE_BLOG_INDEX, null);
  if (!itemAtIndex || itemAtIndex.type !== 'liveBlog') {
    return null;
  }
  return shapeItem(itemAtIndex, packageMetadata);
};

const CoverSpread = (props) => {
  const {
    content,
    content: {
      id: packageId,
      context: {
        isRailAdjacent,
      } = {},
      items = [],
      metadata: packageMetadata = {},
      subType,
    } = {},
  } = props;

  if (!items || !items.length) return null;

  const {
    showBreakingHeader = true,
    title,
    titleUrl,
  } = packageMetadata;

  const isLiveBlog = subType === 'liveBlog';
  const layoutType = isLiveBlog ? 'liveBlog' : 'default';

  const featuredItem = getFeaturedItem(items, packageMetadata);
  let firstTeases = [];
  let secondTeases = [];
  let indexOffset = 0;
  let totalTeases = items.length;
  let liveBlog = null;
  let isLiveBlogBreaking = false;

  if (isLiveBlog) {
    liveBlog = getLiveBlog(items, packageMetadata);
    // filter to remove any articles that are returned as null
    secondTeases = items.filter((item, index) => item && index > LIVE_BLOG_INDEX);

    // TeaseLayout count for live blog based on count of items in column, not total length
    totalTeases = secondTeases.length;

    isLiveBlogBreaking = get(liveBlog, ['metadata', 'showBreakingStyle'], true);
  } else {
    // filter to remove any articles that are returned as null
    // And remove first article since it is pulled into the featured item
    const teases = items.filter((item, index) => item && index > FEATURED_ITEM_INDEX);

    // Split articles into two columns
    firstTeases = teases.slice(0, Math.floor(teases.length / 2));
    secondTeases = teases.slice(Math.floor(teases.length / 2), teases.length);
    indexOffset = firstTeases.length;
  }

  return (
    <div
      className={classNames(
        'pkg cover-spread',
        {
          'cover-spread--default': !isLiveBlog,
          'cover-spread--live-blog': isLiveBlog,
          'cover-spread--rail-adj': isRailAdjacent,
          'cover-spread--breaking': showBreakingHeader !== false,
        },
      )}
      data-test="cover-spread"
      data-packageid={packageId}
    >
      <div
        className={classNames(
          'layout-grid-container',
          {
            'gutter-collapse': isRailAdjacent,
          },
        )}
      >
        <div className="layout-grid-item grid-col-12">
          <div className="cover-spread__headline__wrapper">
            <Title
              title={title}
              titleUrl={titleUrl}
              isRailAdjacent={isRailAdjacent}
            />
          </div>
        </div>

        <div className="layout-grid-item grid-col-12">
          <div className="layout-subgrid-container">
            <div
              className={classNames(
                'cover-spread__column-featured',
                'layout-subgrid-item grid-col-12 grid-col-8-m',
                {
                  'grid-col-12-l': isRailAdjacent,
                  'grid-col-8-l': !isRailAdjacent,
                },
                'grid-col-6-xl',
              )}
            >
              <Featured
                content={featuredItem}
                isRailAdjacent={isRailAdjacent}
                packageMetadata={packageMetadata}
              />
            </div>

            <div
              className={classNames(
                'cover-spread__column-one',
                'layout-subgrid-item grid-col-12 grid-col-4-m',
                {
                  'grid-col-4-l': !isRailAdjacent,
                  'grid-col-6-l': isRailAdjacent,
                },
                'grid-col-3-xl',
              )}
            >
              {(isLiveBlog && liveBlog) && (
                <div
                  className={classNames({
                    'cover-spread__live-blog': isLiveBlog,
                    'cover-spread__live-blog--breaking': isLiveBlogBreaking,
                  })}
                >
                  <LiveBlog
                    // must pass package object as 'content',
                    // so subcomponent knows it's not the root package
                    content={{
                      ...content,
                      items: [{
                        ...liveBlog,
                        metadata: {
                          ...liveBlog.metadata,
                          showDek: false, // dek not supported, override
                        },
                      }],
                    }}
                  />
                </div>
              )}
              {firstTeases.length > 0 && (
                <TeaseLayout
                  columnType="__column--1"
                  isRailAdjacent={isRailAdjacent}
                  layoutType={layoutType}
                  render={(layout) => (
                    firstTeases.map((tease, index) => (
                      <Tease
                        content={tease}
                        index={index}
                        isRailAdjacent={isRailAdjacent}
                        key={`cs-tease-${tease.id}`}
                        layout={layout}
                        layoutType={layoutType}
                        packageMetadata={packageMetadata}
                        showInlineIcon
                      />
                    ))
                  )}
                  totalTeases={items.length}
                />
              )}
            </div>

            <div
              className={classNames(
                'cover-spread__column-two',
                'layout-subgrid-item grid-col-12',
                {
                  'grid-col-12-m': !isLiveBlog,
                  'grid-col-8-m': isLiveBlog,
                  'grid-col-12-l': !isRailAdjacent,
                  'grid-col-6-l': isRailAdjacent,
                },
                'grid-col-3-xl',
              )}
            >
              <TeaseLayout
                columnType="__column--2"
                isRailAdjacent={isRailAdjacent}
                layoutType={layoutType}
                render={(layout) => (
                  secondTeases.map((tease, index) => (
                    <Tease
                      content={tease}
                      index={indexOffset + index}
                      isRailAdjacent={isRailAdjacent}
                      key={`cs-tease-${tease.id}`}
                      layout={layout}
                      layoutType={layoutType}
                      packageMetadata={packageMetadata}
                      showInlineIcon
                    />
                  ))
                )}
                totalTeases={totalTeases}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CoverSpread.propTypes = {
  content: ContentPropType.isRequired,
};

export { CoverSpread };
